'use client';

import { c as _c } from "react/compiler-runtime";
import { translateInline } from '@/globals/translate/translateInline';
import { classes } from '@/uiPrimitives/base/classes.helpers';
import { stack } from '@/uiPrimitives/layout/stack';
import { calculateAllScores, ResultTranslations } from './legalForm.config';
import { AccordionProvider } from '@/uiComposites/interactive/accordion/AccordionProvider.client';
import { box } from '@/uiPrimitives/layout/box';
import { ResultAccordion } from './ResultAccordion';
import { useCurrentQuestion, useGivenAnswers } from './legalFormHooks';
import { DOPLink } from '@/uiComposites/interactive/DOPLink';
import { ChevronAfterLink } from '@/uiComposites/interactive/IconLink';
import { textBlock, textStyle } from '@/uiPrimitives/typography/text';
import { secondaryButtonBox } from '@/uiComposites/interactive/callToAction';
import { shelf } from '@/uiPrimitives/layout/shelf';
import { Sharesheet } from '@/sections/sharesheet/Sharesheet';
import { ReactNode, useEffect, useRef } from 'react';
import { LinkKindIconInline } from '@/uiComposites/interactive/LinkKindIconInline';
import { accordionBorder } from './legalForm.css';
import { indentSize } from '@/uiPrimitives/layout/indentSize.definitions';
import { occupy } from '@/uiPrimitives/layout/occupy';
import { ulMarker } from '@/uiComposites/list/listItem.css';
import { inline } from '@/uiPrimitives/utility/display.css';
import { sharesheetButtonTranslations } from '@/sections/sharesheet/sharesheetTranslations';
import { addTestSelector } from '@/globals/testHelpers/testSelector';

// We don't want capitalization on legal-form names when used inside a sentence
const decapitize = (text: string) => {
  if (text.length === 0) return text;
  return text.charAt(0).toLowerCase() + text.slice(1);
};
export const Results = t0 => {
  const $ = _c(28);
  const {
    results,
    renderedMarkdown,
    pageTitle
  } = t0;
  const givenAnswers = useGivenAnswers();
  const scores = calculateAllScores(givenAnswers);
  const ref = useRef(null);
  const question = useCurrentQuestion();
  let t1;
  let t2;
  if ($[0] !== question) {
    t1 = () => {
      if (question == null) {
        ref.current?.scrollIntoView({
          behavior: "smooth"
        });
        const resultsTitle = document.getElementById("legal-form-results");
        resultsTitle?.focus({
          preventScroll: true
        });
      }
    };
    t2 = [question];
    $[0] = question;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = classes(stack({
      rowGap: "4 | betweenListItems"
    }));
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = addTestSelector("uiScoreContainer");
    t5 = classes(stack({
      rowGap: "1 | h3"
    }));
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  let t6;
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className={t5} id="legal-form-results" tabIndex={-1}><h2><span className={classes(textBlock({
          capSize: "1 | h3",
          fontWeight: "bold"
        }))}>{translateInline({
            en: "Matching legal forms",
            nl: "Passende rechtsvormen"
          })}</span></h2><span className={classes(textBlock({
        capSize: "1 | h3"
      }))}>{translateInline({
          en: "Based on your answers",
          nl: "Gebaseerd op uw antwoorden"
        })}</span></div>;
    t7 = classes(stack({
      gap: "4 | betweenListItems"
    }));
    $[6] = t6;
    $[7] = t7;
  } else {
    t6 = $[6];
    t7 = $[7];
  }
  const T0 = AccordionProvider;
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = classes(stack({
      gap: "0 | p"
    }));
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  const t9 = results.map((result, index) => {
    const score_0 = scores.find(score => score.resultId === result.resultId)?.totalScore || 0;
    return <ResultAccordion score={score_0} key={index} itemId={index} legalFormLabel={result.title} className={accordionBorder}><div className={classes(stack({
        rowGap: "6 | betweenH4Sections"
      }), box({
        paddingInlineStart: "1 | h3"
      }))}><ul className={classes(stack({
          rowGap: "2 | h2"
        }))}>{renderedMarkdown[index].map(_temp)}</ul><ChevronAfterLink href={result.articlePath} data-link-type={`${result.title}-article`} className={textStyle({
          fontWeight: "bold",
          color: "positiveBlue:noVisited"
        })}>{translateInline({
            en: `Read more about the ${decapitize(result.title)}`,
            nl: `Alles over ${decapitize(result.title)}`
          })}</ChevronAfterLink><DOPLink className={classes(secondaryButtonBox({}), shelf({
          gap: "-2 | smaller"
        }))} href={result.registerLink} data-link-type={`${result.title}-register`}><span className={textBlock({})}>{translateInline({
              en: "Register now",
              nl: "Direct inschrijven"
            })}<LinkKindIconInline href={result.registerLink} /></span></DOPLink></div></ResultAccordion>;
  });
  let t10;
  if ($[9] !== t8 || $[10] !== t9) {
    t10 = <div className={t8}>{t9}</div>;
    $[9] = t8;
    $[10] = t9;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] !== T0 || $[13] !== t10) {
    t11 = <T0>{t10}</T0>;
    $[12] = T0;
    $[13] = t10;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <div className={classes(textBlock({}))}>{translateInline({
        en: "Click one of the legal structures if you want to know more or want to make an appointment to register with KVK.",
        nl: "Klik op een van de rechtsvormen als u er meer over wilt weten of een afspraak wilt maken om u in te schrijven bij KVK."
      })}</div>;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  let t13;
  if ($[16] !== pageTitle || $[17] !== question) {
    t13 = question == null && <Sharesheet buttonLabel={sharesheetButtonTranslations["Share results"]} pageTitle={pageTitle} />;
    $[16] = pageTitle;
    $[17] = question;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  if ($[19] !== t11 || $[20] !== t13 || $[21] !== t7) {
    t14 = <div className={t7}>{t11}{t12}{t13}</div>;
    $[19] = t11;
    $[20] = t13;
    $[21] = t7;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== t14 || $[24] !== t3 || $[25] !== t4 || $[26] !== t6) {
    t15 = <div className={t3} ref={ref} {...t4}>{t6}{t14}</div>;
    $[23] = t14;
    $[24] = t3;
    $[25] = t4;
    $[26] = t6;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  return t15;
};
function _temp(item, itemIndex) {
  return <li key={itemIndex}><div className={classes(shelf({
      alignItems: "start"
    }))}><span className={classes(occupy({
        blockSize: "capSize",
        inlineSize: indentSize,
        justifyItems: "start"
      }))}><div className={textBlock({
          fontWeight: "bold"
        })}><i aria-hidden={true} className={ulMarker} /></div></span><div className={inline}>{item}</div></div></li>;
}